import ContainerFilter from "../../ContainerFilter";
import { useFiltersLists } from "core/features/filters/store/lists";
import { useFilters } from "core/features/filters/store";
import OcupacoesButtons from "../../OcupacoesButtons";
import { Flex, IconButton } from "@chakra-ui/react";
import {
  Ocupacoes,
  OperacoesExecutadasOperacoes,
  TecnologiasItem,
} from "core/features/maps/typings";
import CheckboxFiltro from "components/inputs/checkbox-filtro";
import { ButtonClearMapCheckbox } from "../../ButtonLimpar";
import { capitalizeFirstLetter } from "ui/utils";
import { useEffect } from "react";
import { OcupacaoEnum } from "core/features/maps/enums";
import LoadingBox from "components/layout/loading-box";
import NotFoundMapFilters from "components/NotFoundMapFilters";
import useAdvancedFiltersOperacoesExecutadas from "components/maps/base-map/components/advancedFilters/items/hooks/useAdvancedFiltersOperacoesExecutadas";
import { FaChevronUp } from "react-icons/fa";
import MapGridButton from "components/MapGridButton";

interface FiltersOperacoesExecutadasProps {
  isVisible: boolean;
  handleChangeIsVisible: (prop: boolean) => void;
  handleOpenGrid: () => void;
}

function FiltersOperacoesExecutadas({
  isVisible,
  handleChangeIsVisible,
  handleOpenGrid,
}: FiltersOperacoesExecutadasProps) {
  const { filters, updateFiltroOperacoesExecutadas } = useFilters();
  const { operacoesExecutadas, loadings } = useFiltersLists();
  const { clearFilterStates: clearAdvancedFiltersState } =
    useAdvancedFiltersOperacoesExecutadas();

  const { filtroOperacoesExecutadas } = filters;

  const findTecnologiasByOcupacaoSelected = operacoesExecutadas?.ocupacoes.find(
    (item) => item.codigo === filtroOperacoesExecutadas?.ocupacao
  );

  const findOperacoes = operacoesExecutadas?.operacoes.filter(
    (item) => item.ocupacao === filtroOperacoesExecutadas?.ocupacao
  );

  const findOperacoesWithTecnologiaSelected = findOperacoes?.filter((item) =>
    filtroOperacoesExecutadas?.tecnologias.includes(item.tecnologiaId)
  );

  const operacoes =
    findOperacoesWithTecnologiaSelected &&
      findOperacoesWithTecnologiaSelected?.length > 0
      ? findOperacoesWithTecnologiaSelected
      : findOperacoes;

  const removedDuplicateOperacoes = operacoes?.filter(
    (v, i, a) => a.findIndex((t) => t.descricao === v.descricao) === i
  );

  const isCheckedTodos =
    !!filtroOperacoesExecutadas?.operacoes?.length &&
    filtroOperacoesExecutadas?.operacoes?.length ===
    removedDuplicateOperacoes?.length;

  const onClickOperacao = (operacao: OperacoesExecutadasOperacoes) => {
    const formattedOperacao = {
      operacao: operacao.operacaoId,
      color: operacao.cor,
      tecnologiaId: operacao.tecnologiaId,
    };

    const alreadySelected =
      filters.filtroOperacoesExecutadas?.operacoes?.find(
        (op) => operacao.operacaoId === op.operacao
      );

    const updatedOperacoes = alreadySelected
      ? filters.filtroOperacoesExecutadas?.operacoes.filter(
        (op) => op.operacao !== operacao.operacaoId
      )
      : [
        ...(filters.filtroOperacoesExecutadas?.operacoes || []),
        formattedOperacao,
      ];

    updateFiltroOperacoesExecutadas(
      {
        tecnologias: filters.filtroOperacoesExecutadas?.tecnologias,
        ocupacao: filters.filtroOperacoesExecutadas?.ocupacao,
        operacoes: updatedOperacoes,
      },
      true
    );
  };

  const onClearOperacoes = () => {
    updateFiltroOperacoesExecutadas({
      tecnologias: filters.filtroOperacoesExecutadas?.tecnologias,
      ocupacao: filters.filtroOperacoesExecutadas?.ocupacao,
      operacoes: [],
      periodoOperacao: {},
      equipeIds: [],
      insumos: [],
    });
  };

  const formattedOperacoesExecutadasFilters = removedDuplicateOperacoes?.map(
    (item) => ({
      color: item.cor,
      operacao: item.operacaoId,
      tecnologiaId: item.tecnologiaId,
    })
  );

  const selectOcupacao = (ocupacaoId: OcupacaoEnum) => {
    const findOcupacao = operacoesExecutadas?.ocupacoes.find(
      (item) => item.codigo === ocupacaoId
    );
    updateFiltroOperacoesExecutadas({
      ocupacao: findOcupacao?.codigo,
      tecnologias: findOcupacao?.tecnologias.map((i) => i.id),
      operacoes: [],
    });
  };

  useEffect(() => {
    const ocp = operacoesExecutadas?.ocupacoes || [];
    if (!ocp.length) return;

    const firstEnabled = ocp.find((item) => item.enabled);
    if (!firstEnabled) return;

    const expected = {
      ocupacao: firstEnabled.codigo,
      tecnologias: firstEnabled.tecnologias.map((t) => t.id),
    };

    if (
      filtroOperacoesExecutadas?.ocupacao !== expected.ocupacao ||
      JSON.stringify(filtroOperacoesExecutadas?.tecnologias) !==
      JSON.stringify(expected.tecnologias)
    ) {
      updateFiltroOperacoesExecutadas({
        ocupacao: expected.ocupacao,
        tecnologias: expected.tecnologias,
        operacoes: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operacoesExecutadas?.ocupacoes]);

  const updateTecnologiasSelected = (tecnologiaId?: string) => {
    const tecnologiasAtuais = filters.filtroOperacoesExecutadas?.tecnologias || [];
    const newArray = [...tecnologiasAtuais];

    const index = newArray.findIndex((t) => t === tecnologiaId);
    if (index > -1) {
      if (newArray.length === 1) return;
      newArray.splice(index, 1);
    } else if (tecnologiaId) {
      newArray.push(tecnologiaId);
    }

    const foundCompativeOperacoes =
      filters.filtroOperacoesExecutadas?.operacoes.filter((item) =>
        item.tecnologiaId && newArray.includes(item.tecnologiaId)
      );

    const checkTodos =
      findTecnologiasByOcupacaoSelected?.tecnologias
        ?.filter((i) => i.id)
        .map((i) => i.id) ?? [];

    const finalTecnologias = tecnologiaId ? newArray : checkTodos;

    if (
      JSON.stringify(finalTecnologias) !==
      JSON.stringify(filtroOperacoesExecutadas?.tecnologias)
    ) {
      updateFiltroOperacoesExecutadas({
        operacoes: foundCompativeOperacoes,
        ocupacao: filters.filtroOperacoesExecutadas?.ocupacao,
        tecnologias: finalTecnologias,
      });
    }
  };

  const onClickTodos = () => {
    if (isCheckedTodos) {
      updateFiltroOperacoesExecutadas({
        tecnologias: filters.filtroOperacoesExecutadas?.tecnologias,
        ocupacao: filters.filtroOperacoesExecutadas?.ocupacao,
        operacoes: [],
        periodoOperacao: {},
        insumos: [],
        equipeIds: [],
      });
      setTimeout(() => {
        clearAdvancedFiltersState();
      }, 2000);
    } else {
      updateFiltroOperacoesExecutadas(
        {
          tecnologias: filters.filtroOperacoesExecutadas?.tecnologias,
          ocupacao: filters.filtroOperacoesExecutadas?.ocupacao,
          operacoes: formattedOperacoesExecutadasFilters,
        },
        true
      );
    }
  };

  if (loadings.operacoesExecutadas) {
    return <LoadingBox isVisible={isVisible} />;
  }

  if (!operacoesExecutadas && !loadings.operacoesExecutadas) {
    return (
      <ContainerFilter
        containerProps={{
          mt: 3,
          rounded: 8,
          padding: 4,
        }}
      >
        <Flex pl={1} mb={1}>
          <NotFoundMapFilters message="Não foram encontradas ocupações para o filtro selecionado" />
        </Flex>
        <Flex
          padding="5px"
          flexDirection="row-reverse"
          borderTop="1px solid #84848440"
          marginTop="10px"
        >
          <IconButton
            background="none"
            icon={<FaChevronUp color="#848484" />}
            aria-label="close-button"
            marginRight="20px"
            onClick={() => handleChangeIsVisible(false)}
          />
        </Flex>
      </ContainerFilter>
    );
  }

  return (
    <ContainerFilter
      containerProps={{ mt: 3, rounded: 8, padding: 4 }}
      isVisible={isVisible}
    >
      <OcupacoesButtons
        onChange={selectOcupacao}
        value={filters.filtroOperacoesExecutadas?.ocupacao!}
        items={operacoesExecutadas?.ocupacoes as Ocupacoes[]}
        tecnologiasValue={filters.filtroOperacoesExecutadas?.tecnologias ?? []}
        tecnologiasItems={
          findTecnologiasByOcupacaoSelected?.tecnologias as TecnologiasItem[]
        }
        onChangeTecnologias={updateTecnologiasSelected}
      />

      {!loadings.operacoesExecutadas && (
        <>
          <Flex h="1px" bg="gray.200" mt={4} flex={1} />

          <Flex mt={4} flexDir="column" ml={2}>
            {removedDuplicateOperacoes &&
              !removedDuplicateOperacoes?.length && (
                <Flex>
                  <NotFoundMapFilters message="Não foram encontradas operações para o filtro selecionado" />
                </Flex>
              )}

            <Flex>
              {removedDuplicateOperacoes?.length ? (
                <>
                  <CheckboxFiltro
                    disabled={false}
                    onClick={onClickTodos}
                    isChecked={isCheckedTodos}
                    text="Todas"
                    data-testid="menu-filtros-operacoes-executadas-todos"
                  />
                  <ButtonClearMapCheckbox onClear={onClearOperacoes} />
                </>
              ) : null}
            </Flex>

            {removedDuplicateOperacoes && removedDuplicateOperacoes?.length > 0 &&
              removedDuplicateOperacoes.map((operacao, index) => (
                <CheckboxFiltro
                  key={operacao.operacaoId + index}
                  text={capitalizeFirstLetter(operacao.descricao)}
                  onClick={() => onClickOperacao(operacao)}
                  color={operacao.cor}
                  data-testid={`menu-filtro-operacoes-executadas-${operacao.descricao}`}
                  isChecked={
                    !!filters.filtroOperacoesExecutadas?.operacoes?.find(
                      (op) => op.operacao === operacao.operacaoId
                    )
                  }
                />
              ))}
          </Flex>
        </>
      )}

      <Flex
        padding="5px"
        justifyContent="space-between"
        borderTop="1px solid #84848440"
        alignItems="center"
        paddingTop="15px"
        marginTop="10px"
      >
        <MapGridButton handleOpenGrid={handleOpenGrid} />
        <IconButton
          background="none"
          icon={<FaChevronUp color="#848484" />}
          aria-label="close-button"
          marginRight="20px"
          onClick={() => handleChangeIsVisible(false)}
        />
      </Flex>
    </ContainerFilter>
  );
}

export default FiltersOperacoesExecutadas;
